import { format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isToday, isSameDay, addMonths, isSameMonth } from 'date-fns';
import { Clock, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import Loader from '../../General/Loader';
import { useEffect, useState } from 'react';

export default function Calendar({
    currentMonth, 
    setCurrentMonth,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    setSelectedSlot,
    availableSlots,
    isLoading
}) {
    const [allDayAvailable, setAllDayAvailable] = useState(false) 

    useEffect(() => {
        setAllDayAvailable(false)
    }, [availableSlots])

    const navigateMonth = (direction) => {
        setCurrentMonth(prev => addMonths(prev, direction === 'next' ? 1 : -1));
    };

    const getMonthDays = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);
        const start = startOfWeek(monthStart, { weekStartsOn: 0 });
        const end = endOfWeek(monthEnd, { weekStartsOn: 0 });
    
        const days = [];
        for (let date = start; date <= end; date = addDays(date, 1)) {
          days.push(date);
        }
    
        return days;
      };
    
      const isSlotAvailableByDate = (date, period) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return availableSlots.some(slot => slot.date === dateStr && slot.period === period)
      };

      const getSlotAvailable = (date, period) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return availableSlots.find(slot => slot.date === dateStr && slot.period === period)
      };
    
    
      const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedTime(null);
    
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      };

    useEffect(() => {
        // case when there is full day class OR a few with AM and PM
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const allSlotsForDate = availableSlots.filter(slot => slot.date === dateStr)
        const allTimeWindowsForDate = allSlotsForDate.map(slot => slot.period)

        if (allTimeWindowsForDate.includes('full_day')) {
            setAllDayAvailable(true)
            
            const slotAvailable = getSlotAvailable(selectedDate, 'full_day')
            setSelectedSlot(slotAvailable)
            setSelectedTime('full_day')
        } else {
            // case when there is AM or PM only:
            const isAvailableAM = isSlotAvailableByDate(selectedDate, 'AM')
            const isAvailablePM = isSlotAvailableByDate(selectedDate, 'PM')
            const isOneOnlyAvailable = !(isAvailableAM && isAvailablePM)
            if (isOneOnlyAvailable) {
                if (isAvailableAM) {
                    const slotAvailable = getSlotAvailable(selectedDate, 'AM')
                    setSelectedSlot(slotAvailable)
                    setSelectedTime('AM')
                } else if (isAvailablePM) {
                    const slotAvailable = getSlotAvailable(selectedDate, 'PM')
                    setSelectedSlot(slotAvailable)
                    setSelectedTime('PM')
                }
            }
        }
    }, [selectedDate])

    const timeWindowFullDayTemplate = () => {
        return (
            <div className="grid grid-cols-1 gap-3">
                <button
                    onClick={() => setSelectedTime('full_day')}
                    className="p-4 rounded-lg border-2 transition-all border-green-600 bg-green-50"
                >
                    <div className="font-semibold">Full Day</div>
                    <div className="text-sm text-gray-600">7:00 AM - 5:00 PM</div>
                </button>
            </div>
        )
    }

    const timeWindowMorningEveningTemplate = () => {
        return (
            <div className="grid grid-cols-2 gap-3">
                {['AM', 'PM'].map(period => {
                    const isAvailable = isSlotAvailableByDate(selectedDate, period);
                    const isTimeSelected = selectedTime === period;
                    
                    const slotAvailable = getSlotAvailable(selectedDate, period)
                    return (
                    <button
                        key={period}
                        onClick={() => {
                            setSelectedSlot(slotAvailable)
                            setSelectedTime(period)
                        }}
                        disabled={!isAvailable}
                        className={`p-4 rounded-lg border-2 transition-all
                        ${isTimeSelected && "border-green-600 bg-green-50"}
                        ${!isTimeSelected && isAvailable && "border-gray-200 hover:border-green-400"}
                        ${!isAvailable && "border-gray-100 bg-gray-50 cursor-not-allowed opacity-50"}`}
                    >
                        <div className="font-semibold">{period}</div>
                        <div className="text-sm text-gray-600">
                        {period === 'AM' ? '7:00 AM - 12:00 PM' : '12:00 PM - 5:00 PM'}
                        </div>
                        {!isAvailable && (
                        <div className="text-xs text-gray-500 mt-1">Not Available</div>
                        )}
                    </button>
                    );
                })}
                </div>
        )
    }

    const dayCardTemplate = (date, index) => {
        const isSelected = selectedDate && isSameDay(date, selectedDate);
        const isPast = date < new Date() && !isToday(date);
        const isCurrentMonth = isSameMonth(date, currentMonth);
        const hasAM = isSlotAvailableByDate(date, 'AM');
        const hasPM = isSlotAvailableByDate(date, 'PM');
        const hasFullDay = isSlotAvailableByDate(date, 'full_day');
        const hasAvailability = hasFullDay || (hasAM || hasPM)
        const availability = availableSlots.filter(slot => slot.date === format(date, 'yyyy-MM-dd'))
  
        return (
            <button
                key={index}
                onClick={() => !isPast && hasAvailability && isCurrentMonth && handleDateSelect(date)}
                disabled={isPast || !hasAvailability || !isCurrentMonth}
                className={`aspect-square p-1 rounded-lg transition-all relative text-sm flex flex-col items-center justify-center min-h-[40px]
                    ${isSelected && "bg-green-600 text-white"}
                    ${!isSelected && hasAvailability && isCurrentMonth && "hover:bg-gray-100"}
                    ${!isCurrentMonth && "text-gray-300"}
                    ${isPast && isCurrentMonth && "opacity-50 cursor-not-allowed"}
                    ${!hasAvailability && isCurrentMonth && !isPast && "opacity-30 cursor-not-allowed"}
                    ${isToday(date) && !isSelected && "bg-blue-50 border border-blue-200"}
                    ${hasAvailability && isCurrentMonth && !isPast && "bg-green-50 border border-green-200"}`}
            >
                <div className="font-medium">{format(date, 'd')}</div>
                    {hasAvailability && isCurrentMonth && !isPast && (
                        <div className="flex gap-1 mt-1">
                            {hasAM && <div className="w-1 h-1 bg-green-500 rounded-full" />}
                            {hasPM && <div className="w-1 h-1 bg-blue-500 rounded-full" />}
                        </div>
                    )}
            </button>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    disabled={isSameMonth(currentMonth, new Date())}
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold text-lg">
                    {format(currentMonth, 'MMMM yyyy')}
                </h3>
                <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            </div>
            <Loader isLoading={isLoading}>
                <div className="grid grid-cols-7 gap-1 mb-6">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 p-2">
                    {day}
                    </div>
                ))}
                {getMonthDays().map((date, index) => dayCardTemplate(date, index))}
                </div>
            </Loader>

            {selectedDate && (
            <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Available Times for {format(selectedDate, 'MMMM d, yyyy')}
                </h4>
                    {allDayAvailable ? timeWindowFullDayTemplate() : timeWindowMorningEveningTemplate()}
                
                <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">
                    You'll receive a more specific time window via text and email once your appointment is routed
                </p>
                </div>
            </div>
            )}
      </div>
    )
}

// const generateMockSlots = () => {
//     const slots = [];
//     const today = new Date();

//     for (let i = 1; i <= 30; i++) {
//       const date = addDays(today, i);
//       const dateStr = format(date, 'yyyy-MM-dd');

//       if (date.getDay() !== 0) {
//         if (Math.random() > 0.3) {
//           slots.push({
//             id: `${dateStr}-am`,
//             date: dateStr,
//             startTime: '07:00',
//             endTime: '12:00',
//             period: 'AM',
//           });
//         }
//         if (Math.random() > 0.3) {
//           slots.push({
//             id: `${dateStr}-pm`,
//             date: dateStr,
//             startTime: '12:00',
//             endTime: '17:00',
//             period: 'PM',
//           });
//         }
//       }
//     }

//     setAvailableSlots(slots);
//   };